import type { PayslipDetail, CompareResult, MetricDelta, ComponentDelta, ComponentType } from '../types';

function delta(current: number, previous: number): MetricDelta {
  const deltaAmount = current - previous;
  const deltaPct = previous !== 0 ? (deltaAmount / previous) * 100 : null;
  return { current, previous, deltaAmount, deltaPct };
}

function flatten(d: PayslipDetail): Map<string, { label: string; type: ComponentType; amount: number }> {
  const m = new Map<string, { label: string; type: ComponentType; amount: number }>();
  for (const g of d.categories) {
    for (const it of g.items) m.set(it.code, { label: it.label, type: it.componentType, amount: it.amount });
  }
  return m;
}

const pct = (m: MetricDelta) => (m.deltaPct != null ? `${Math.abs(m.deltaPct).toFixed(1)}%` : '');

/** Compute the period-over-period comparison entirely client-side from two
 *  payslip details. The narrative uses percentages + direction only (no
 *  sensitive absolute amounts), and explains — never recomputes. */
export function computeCompare(current: PayslipDetail, previous: PayslipDetail): CompareResult {
  const net = delta(current.net, previous.net);
  const gross = delta(current.gross, previous.gross);
  const deductions = delta(current.totalDeductions, previous.totalDeductions);
  const contributions = delta(current.totalEmployerContributions, previous.totalEmployerContributions);

  const a = flatten(current);
  const b = flatten(previous);
  const codes = new Set<string>([...a.keys(), ...b.keys()]);
  const componentDeltas: ComponentDelta[] = [];
  codes.forEach((code) => {
    const ca = a.get(code);
    const cb = b.get(code);
    const d = delta(ca?.amount ?? 0, cb?.amount ?? 0);
    if (Math.abs(d.deltaAmount) < 0.005) return;
    componentDeltas.push({
      code,
      label: ca?.label ?? cb?.label ?? code,
      componentType: (ca?.type ?? cb?.type) as ComponentType,
      ...d,
    });
  });
  componentDeltas.sort((x, y) => Math.abs(y.deltaAmount) - Math.abs(x.deltaAmount));

  const narrative: string[] = [];
  if (Math.abs(gross.deltaAmount) >= 0.005) {
    narrative.push(`Gross pay ${gross.deltaAmount > 0 ? 'rose' : 'fell'} ${pct(gross)}, which moved your statutory deductions.`);
  }
  componentDeltas
    .filter((c) => c.componentType !== 'EMPLOYER_CONTRIBUTION')
    .slice(0, 2)
    .forEach((c) => {
      narrative.push(`${c.label} ${c.deltaAmount > 0 ? 'increased' : 'decreased'}${pct(c) ? ` ${pct(c)}` : ''}.`);
    });
  if (narrative.length === 0) narrative.push('No material changes between these statements.');

  return { currency: current.currency, net, gross, deductions, contributions, componentDeltas, narrative };
}
